import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useUser } from "@clerk/react";
import { safeSetItem } from "@/lib/safeStorage";
import { Users, Shield, Leaf, ChevronRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { fadeUp, fadeIn, staggerContainer, staggerItem, instant } from "@/lib/animations";

const SAGE = "#A8B5A2";
const TERRACOTTA = "#D9B8A3";
const CLAY = "#E8DAB2";

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  useEffect(() => {
    function onResize() { setIsDesktop(window.innerWidth >= 1024); }
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktop;
}

export default function HowItWorksPage() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { isSignedIn } = useUser();
  const isDesktop = useIsDesktop();
  const reduce = useReducedMotion();

  const fadeUpV  = reduce ? instant : fadeUp;
  const fadeInV  = reduce ? instant : fadeIn;
  const staggerC = reduce ? instant : staggerContainer;
  const staggerI = reduce ? instant : staggerItem;

  function navigateAsDonor() {
    if (isSignedIn) navigate("/donor");
    else { safeSetItem("pendingRole", "donor"); navigate("/sign-up"); }
  }

  function navigateAsDebtor() {
    if (isSignedIn) navigate("/debtor");
    else { safeSetItem("pendingRole", "debtor"); navigate("/sign-up"); }
  }

  const pillBtn: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "13px 28px",
    borderRadius: "50px",
    fontSize: "14px",
    fontWeight: 300,
    letterSpacing: "0.04em",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "1px solid transparent",
  };

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))", fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
      <SiteNav />

      {/* Page header */}
      <motion.section
        variants={staggerC}
        initial="hidden"
        animate="visible"
        style={{ padding: "80px 20px 20px", textAlign: "center", maxWidth: "680px", margin: "0 auto" }}
      >
        <motion.p variants={fadeInV} style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: TERRACOTTA, marginBottom: "10px" }}>
          {t("howItWorks.eyebrow")}
        </motion.p>
        <motion.h1 variants={fadeUpV} style={{ fontSize: "clamp(28px, 5vw, 52px)", fontWeight: 200, letterSpacing: "-0.03em", marginBottom: "16px", lineHeight: 1.1 }}>
          {t("howItWorks.headline")}
        </motion.h1>
        <motion.p variants={fadeUpV} style={{ color: "hsl(var(--muted-foreground))", fontWeight: 300, fontSize: "16px", lineHeight: 1.7 }}>
          {t("howItWorks.subtext")}
        </motion.p>
      </motion.section>

      {/* Steps + image */}
      <section style={{ position: "relative", padding: "48px 20px 80px", overflow: "hidden" }}>
        <div aria-hidden style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(135deg, rgba(217,184,163,0.07) 0%, rgba(232,218,178,0.04) 100%)`,
          pointerEvents: "none",
        }} />
        <svg aria-hidden style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}>
          <circle cx="90%" cy="15%" r="190" fill="none" stroke={CLAY} strokeWidth="0.6" opacity="0.08" />
          <circle cx="8%" cy="85%" r="130" fill="none" stroke={TERRACOTTA} strokeWidth="0.5" opacity="0.06" />
        </svg>

        <div style={{ position: "relative", maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: isDesktop ? "1fr 420px" : "1fr",
            gap: "40px",
            alignItems: "start",
          }}>
            <motion.div
              variants={staggerC}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px" }}
            >
              {[
                { step: "01", icon: Users, title: t("howItWorks.step1Title"), desc: t("howItWorks.step1Desc"), accent: SAGE },
                { step: "02", icon: Shield, title: t("howItWorks.step2Title"), desc: t("howItWorks.step2Desc"), accent: TERRACOTTA },
                { step: "03", icon: Leaf, title: t("howItWorks.step3Title"), desc: t("howItWorks.step3Desc"), accent: CLAY },
              ].map(({ step, icon: Icon, title, desc, accent }) => (
                <motion.div
                  key={step}
                  variants={staggerI}
                  className="glass-card"
                  style={{
                    display: "flex",
                    gap: "20px",
                    padding: "28px",
                    borderRadius: "16px",
                    cursor: "default",
                  }}
                >
                  <div style={{ flexShrink: 0, padding: "10px", borderRadius: "10px", background: `${accent}20`, border: `1px solid ${accent}50`, height: "fit-content" }}>
                    <Icon style={{ width: "18px", height: "18px", color: accent }} />
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <span style={{ fontSize: "10px", fontWeight: 400, color: accent, letterSpacing: "0.1em" }}>{step}</span>
                      <h3 style={{ fontSize: "15px", fontWeight: 400, letterSpacing: "-0.01em", margin: 0 }}>{title}</h3>
                    </div>
                    <p style={{ fontSize: "14px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.7, margin: 0 }}>{desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {isDesktop && (
              <motion.div
                variants={fadeInV}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                style={{
                  position: "sticky",
                  top: "88px",
                  borderRadius: "20px",
                  overflow: "hidden",
                  height: "420px",
                }}
              >
                <img
                  src="https://images.unsplash.com/photo-1514924013411-cbf25faa35bb?w=840&q=80&fit=crop&auto=format"
                  alt="Suspension bridge at dusk"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  loading="lazy"
                />
                <div aria-hidden style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(160deg, rgba(168,181,162,0.25) 0%, rgba(10,10,10,0.45) 100%)",
                }} />
                <div style={{ position: "absolute", bottom: "24px", left: "24px", right: "24px" }}>
                  <p style={{ color: "#ffffff", fontSize: "13px", fontWeight: 300, lineHeight: 1.5, margin: 0, textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}>
                    {t("howItWorks.quote")}
                  </p>
                  <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "11px", fontWeight: 300, marginTop: "4px", letterSpacing: "0.04em" }}>
                    {t("howItWorks.quoteAttrib")}
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ / extra detail */}
      <section style={{ padding: "60px 20px 80px", background: "#0a0a0a", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse 50% 40% at 30% 50%, rgba(168,181,162,0.05) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />
        <div style={{ position: "relative", maxWidth: "760px", margin: "0 auto" }}>
          <motion.p
            variants={fadeInV}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: SAGE, marginBottom: "16px" }}
          >
            Common questions
          </motion.p>
          <motion.div
            variants={staggerC}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            style={{ display: "grid", gap: "24px" }}
          >
            {[
              { q: "Where does my donation actually go?", a: "100% of your donation is placed into a designated pool. When the pool reaches its threshold, DebtBridge pays the creditor directly and eliminates the debt. Nothing is taken for overhead." },
              { q: "How do you verify the debt is real?", a: "Every applicant submits documentation of their debt — statements, notices, court records. Our team reviews each case before creating a pool. We only list verified balances." },
              { q: "What kinds of debt can be relieved?", a: "Medical debt, student loans, credit card debt, auto loans, housing debt, and more. Each pool is categorised so donors can choose causes they care most about." },
              { q: "Is my donation tax-deductible?", a: "Yes. DebtBridge is a registered 501(c)(3) nonprofit. You will receive a donation receipt for your records. See our Tax Guide for details." },
            ].map(({ q, a }) => (
              <motion.div key={q} variants={staggerI} style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: "24px" }}>
                <h3 style={{ fontSize: "15px", fontWeight: 400, color: "#ffffff", marginBottom: "10px", letterSpacing: "-0.01em" }}>{q}</h3>
                <p style={{ fontSize: "14px", fontWeight: 300, color: "rgba(255,255,255,0.45)", lineHeight: 1.75, margin: 0 }}>{a}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <motion.section
        variants={staggerC}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        style={{ padding: "80px 20px", textAlign: "center" }}
      >
        <motion.h2 variants={fadeUpV} style={{ fontSize: "clamp(24px, 3.5vw, 38px)", fontWeight: 200, letterSpacing: "-0.03em", marginBottom: "12px" }}>
          Ready to make a difference?
        </motion.h2>
        <motion.p variants={fadeUpV} style={{ color: "hsl(var(--muted-foreground))", fontWeight: 300, fontSize: "15px", marginBottom: "36px", maxWidth: "440px", margin: "0 auto 36px", lineHeight: 1.7 }}>
          Browse open pools and donate today, or apply to have your debt relieved by the community.
        </motion.p>
        <motion.div variants={fadeUpV} style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <button
            className="btn-shimmer"
            style={{ ...pillBtn, background: "#0a0a0a", color: "#ffffff", border: "1px solid #0a0a0a" }}
            onClick={navigateAsDonor}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 3px rgba(168,181,162,0.35)`; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
          >
            Browse Pools <ChevronRight size={14} />
          </button>
          <button
            className="btn-shimmer"
            style={{ ...pillBtn, background: "transparent", color: "rgba(10,10,10,0.60)", border: "1px solid rgba(10,10,10,0.20)" }}
            onClick={navigateAsDebtor}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(10,10,10,0.05)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
          >
            Apply for Relief
          </button>
        </motion.div>
      </motion.section>

      <SiteFooter />
    </div>
  );
}
